import type { MedicationRequest } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { Participant } from '@seen/core/models/patients/participant'
import { queuedEditsOriginTaskFromResource } from '@seen/core/queued-edits/origin'
import { createReference } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import type { ReactNode } from 'react'
import { TextLink } from '#src/components/index.ts'
import { useCreatedBy } from '#src/hooks/useCreatedBy.ts'
import { useCreatedOn } from '#src/hooks/useCreatedOn.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { TimelineTextEntry } from '#src/pages/participants/timeline/TimelineTextEntry.tsx'

interface MedicationRequestItemProps {
  medicationRequest: MedicationRequest
}

export function MedicationRequestItem({
  medicationRequest,
}: MedicationRequestItemProps): ReactNode {
  const { data: participant } = useRead(
    Participant,
    isOfType(medicationRequest.subject, 'Patient')
      ? medicationRequest.subject
      : undefined
  )

  const title = medicationRequest.medicationReference
    ? referenceDisplay(medicationRequest.medicationReference)
    : (medicationRequest.medicationCodeableConcept?.text ??
      'Unknown Medication')

  useErrorToast(
    !medicationRequest.authoredOn &&
      `MedicationRequest/${medicationRequest.id} missing authoredOn`
  )

  const { data: createdOn, isLoading: createdOnIsLoading } = useCreatedOn(
    createReference(medicationRequest)
  )
  const { data: createdBy, isLoading: createdByIsLoading } = useCreatedBy(
    createReference(medicationRequest)
  )

  let action = 'prescribed'
  if (medicationRequest.intent === 'plan') {
    action = 'documented'
  }

  return (
    <TimelineTextEntry
      actor={medicationRequest.requester ?? createdBy ?? 'Unknown'}
      actorIsLoading={!medicationRequest.requester && createdByIsLoading}
      time={medicationRequest.authoredOn ?? createdOn ?? null}
      timeIsLoading={!medicationRequest.authoredOn && createdOnIsLoading}
      action={
        <>
          {action}{' '}
          <TextLink
            to={`/Patient/${participant?.id}/medications/${medicationRequest.id}`}
          >
            {title}
          </TextLink>
        </>
      }
      reference={createReference(medicationRequest)}
      originTask={queuedEditsOriginTaskFromResource(medicationRequest)}
    />
  )
}
